<div class="task-details">
  <header class="task-details__header">
    <div class="task-details__title-group">
      <span class="task-status" [attr.data-status]="taskSignal()?.status">{{ statusLabel() }}</span>
      @if (taskSignal()?.task_code) {
        <p>{{ taskSignal()?.task_code?.toUpperCase() }}</p>
      }
      <h2>{{ taskSignal()?.title || 'Task details' }}</h2>
      @if (taskSignal()?.description) {
        <div class="task-details__description" [innerHTML]="renderDescription(taskSignal()?.description)"></div>
      } @else {
        <p>No description yet.</p>
      }
    </div>

    <div class="task-details__header-actions">
      @if (canEdit && taskSignal()) {
        <button type="button" mat-stroked-button (click)="openEditDialog()">Edit</button>
      }
      <button type="button" mat-icon-button aria-label="Close task details" (click)="closed.emit()">
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
    </div>
  </header>

  @if (loading()) {
    <div class="task-details__state">Loading task details…</div>
  } @else if (!taskSignal()) {
    <div class="task-details__state">Task details are unavailable.</div>
  } @else {
        <div class="task-details__layout">
          <section class="task-details__main">
            @if (canEdit) {
              <section class="task-details__section">
                <div class="task-details__section-head">
                  <h3>Quick edit</h3>
                </div>

                <div class="task-details__quick-grid">
                  <mat-form-field appearance="outline">
                    <mat-label>Status</mat-label>
                    <mat-select [value]="taskSignal()?.status" [disabled]="saving()" (valueChange)="updateQuickField('status', $event)">
                      @for (status of statuses; track status.key) {
                        <mat-option [value]="status.key">{{ status.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Priority</mat-label>
                    <mat-select [value]="taskSignal()?.priority" [disabled]="saving()" (valueChange)="updateQuickField('priority', $event)">
                      @for (priority of priorities; track priority.value) {
                        <mat-option [value]="priority.value">{{ priority.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Start date</mat-label>
                    <input #startDateInput matInput type="date" [value]="toInputDate(taskSignal()?.start_at)" [disabled]="saving()" (change)="updateQuickField('start_at', toIsoDate(startDateInput.value))" />
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Due date</mat-label>
                    <input #dueDateInput matInput type="date" [value]="toInputDate(taskSignal()?.due_at)" [disabled]="saving()" (change)="updateQuickField('due_at', toIsoDate(dueDateInput.value))" />
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Estimate (minutes)</mat-label>
                    <input #estimateInput matInput type="number" min="0" [value]="taskSignal()?.estimated_minutes ?? 0" [disabled]="saving()" (change)="updateQuickField('estimated_minutes', estimateInput.value)" />
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Tracked (minutes)</mat-label>
                    <input #trackedInput matInput type="number" min="0" [value]="taskSignal()?.tracked_minutes ?? 0" [disabled]="saving()" (change)="updateQuickField('tracked_minutes', trackedInput.value)" />
                  </mat-form-field>
                </div>
              </section>
            }

            <div class="task-details__meta-grid">
          <article class="task-details__meta-card">
            <span>Subtask owners</span>
            <strong>{{ getTaskAssigneeNames(taskSignal()) }}</strong>
          </article>
          <article class="task-details__meta-card">
            <span>Start date</span>
            <strong>{{ formatDueDate(taskSignal()?.start_at ?? null) }}</strong>
          </article>
          <article class="task-details__meta-card">
            <span>Due date</span>
            <strong>{{ formatDueDate(taskSignal()?.due_at ?? null) }}</strong>
          </article>
          <article class="task-details__meta-card">
            <span>Priority</span>
            <strong class="task-priority" [attr.data-priority]="taskSignal()?.priority">{{ taskSignal()?.priority }}</strong>
          </article>
          <article class="task-details__meta-card">
            <span>Estimate</span>
            <strong>{{ formatMinutes(taskSignal()?.estimated_minutes) }}</strong>
          </article>
          <article class="task-details__meta-card">
            <span>Tracked</span>
            <strong>{{ formatMinutes(taskSignal()?.tracked_minutes) }}</strong>
          </article>
          <article class="task-details__meta-card">
            <span>Tags</span>
            <strong>{{ formatTags(taskSignal()?.tags) || 'None' }}</strong>
          </article>
        </div>

        <section class="task-details__section">
          <div class="task-details__section-head">
            <h3>Attachments</h3>
            <span>{{ attachments().length }}</span>
          </div>

          @if (canEdit) {
            <label class="task-details__attachment-upload">
              <input type="file" hidden accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" [disabled]="uploadingAttachment() || saving()" (change)="onAttachmentSelected($event)" />
              <mat-icon aria-hidden="true">{{ uploadingAttachment() ? 'hourglass_top' : 'attach_file' }}</mat-icon>
              <span>{{ uploadingAttachment() ? 'Uploading attachment…' : 'Add attachment' }}</span>
            </label>
          }

          <div class="task-details__attachments">
            @for (attachment of attachments(); track attachment.id) {
              <article class="task-details__attachment">
                @if (isImageAttachment(attachment) && attachment.url) {
                  <button type="button" class="task-details__attachment-preview" (click)="openAttachment(attachment)">
                    <img [src]="attachment.url" [alt]="attachment.name || 'Attachment preview'" />
                  </button>
                } @else {
                  <div class="task-details__attachment-file-icon">
                    <mat-icon aria-hidden="true">description</mat-icon>
                  </div>
                }

                <div class="task-details__attachment-copy">
                  <strong>{{ attachment.name }}</strong>
                  <span>{{ formatAttachmentSize(attachment.size) || attachment.type || 'File' }}</span>
                </div>

                <div class="task-details__attachment-actions">
                  <button type="button" mat-button (click)="openAttachment(attachment)">Open</button>
                  <button type="button" mat-button (click)="downloadAttachment(attachment)">Download</button>
                </div>
              </article>
            } @empty {
              <div class="task-details__empty">No attachments yet.</div>
            }
          </div>
        </section>

        <section class="task-details__section">
          <div class="task-details__section-head">
            <h3>Time tracking</h3>
            <span>{{ timeEntries().length }}</span>
          </div>

          <div class="task-details__timer-bar">
            <div class="task-details__timer-state">
              <strong>{{ activeTimer() ? 'Timer running' : 'No active timer' }}</strong>
              <span>{{ activeTimer() ? formatTimeEntryRange(activeTimer()!) : (canManageTimeTracking() ? 'Start a timer or log time manually.' : 'Only the assigned subtask owner can log time.') }}</span>
              @if (activeTimer()) {
                <small>{{ formatTimeEntrySubtask(activeTimer()!) }}</small>
              }
            </div>
            <div class="task-details__timer-actions">
              @if (!activeTimer() && availableTimeLoggingSubtasks().length) {
                <mat-form-field appearance="outline" class="task-details__timer-subtask-field">
                  <mat-label>Subtask</mat-label>
                  <mat-select [value]="timerSubtaskId()" (valueChange)="timerSubtaskId.set($event)">
                    @for (subtask of availableTimeLoggingSubtasks(); track subtask.id) {
                      <mat-option [value]="subtask.id">{{ subtask.title }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              }
              @if (!activeTimer()) {
                <button type="button" mat-flat-button color="primary" [disabled]="saving() || !canManageTimeTracking()" (click)="startTimer()">Start timer</button>
              } @else {
                <button type="button" mat-stroked-button [disabled]="saving() || !canManageTimeEntry(activeTimer()!)" (click)="stopTimer()">Stop timer</button>
              }
            </div>
          </div>

          @if (canEdit && canManageTimeTracking()) {
            <form [formGroup]="timeEntryForm" class="task-details__inline-form task-details__inline-form--time" (ngSubmit)="addTimeEntry()">
              <mat-form-field appearance="outline" class="task-details__time-subtask-field">
                <mat-label>Subtask</mat-label>
                <mat-select formControlName="subtask_id">
                  @for (subtask of availableTimeLoggingSubtasks(); track subtask.id) {
                    <mat-option [value]="subtask.id">{{ subtask.title }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="task-details__time-minutes-field">
                <mat-label>Minutes</mat-label>
                <input matInput type="number" min="1" formControlName="minutes" />
              </mat-form-field>
              <mat-form-field appearance="outline" class="task-details__time-note-field">
                <mat-label>Note</mat-label>
                <input matInput formControlName="note" />
              </mat-form-field>
              <button type="submit" mat-flat-button color="primary" class="task-details__time-submit" [disabled]="timeEntryForm.invalid || saving()">Log time</button>
            </form>
          }

          <div class="task-details__time-list">
            @for (entry of timeEntries(); track entry.id) {
              <article class="task-details__time-entry">
                @if (editingTimeEntryId() === entry.id) {
                  <form [formGroup]="timeEntryEditForm" class="task-details__time-edit-form" (ngSubmit)="saveTimeEntry(entry)">
                    <mat-form-field appearance="outline">
                      <mat-label>Subtask</mat-label>
                      <mat-select formControlName="subtask_id">
                        @for (subtask of availableTimeLoggingSubtasks(); track subtask.id) {
                          <mat-option [value]="subtask.id">{{ subtask.title }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Minutes</mat-label>
                      <input matInput type="number" min="1" formControlName="minutes" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Note</mat-label>
                      <input matInput formControlName="note" />
                    </mat-form-field>
                    <div class="task-details__time-edit-actions">
                      <button type="submit" mat-button [disabled]="timeEntryEditForm.invalid || saving()">Save</button>
                      <button type="button" mat-button (click)="cancelTimeEntryEdit()">Cancel</button>
                    </div>
                  </form>
                } @else {
                  <div class="task-details__time-entry-summary">
                    <div>
                      <strong>{{ formatMinutes(entry.minutes) }}</strong>
                      <span>{{ formatTimeEntryRange(entry) }}</span>
                    </div>
                    @if (entry.note) {
                      <p class="task-details__time-entry-note">{{ entry.note }}</p>
                    }
                  </div>
                  <div class="task-details__time-entry-meta">
                    <small>{{ getUserName(entry.created_by) }}</small>
                    <small>{{ formatTimeEntrySubtask(entry) }}</small>
                  </div>
                  @if (canEdit && entry.ended_at && canManageTimeEntry(entry)) {
                    <div class="task-details__time-entry-actions">
                      <button type="button" mat-icon-button aria-label="Edit time entry" (click)="startTimeEntryEdit(entry)">
                        <mat-icon aria-hidden="true">edit</mat-icon>
                      </button>
                      <button type="button" mat-icon-button aria-label="Delete time entry" (click)="deleteTimeEntry(entry)">
                        <mat-icon aria-hidden="true">delete</mat-icon>
                      </button>
                    </div>
                  }
                }
              </article>
            } @empty {
              <div class="task-details__empty">No time tracked yet.</div>
            }
          </div>
        </section>

        <section class="task-details__section">
          <div class="task-details__section-head">
            <h3>Subtasks</h3>
            <span>{{ completedSubtasks() }}/{{ subtasks().length }}</span>
          </div>

          @if (canEdit) {
            <form [formGroup]="subtaskForm" class="task-details__inline-form" (ngSubmit)="addSubtask()">
              <mat-form-field appearance="outline">
                <mat-label>Add subtask</mat-label>
                <input matInput formControlName="title" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Assignee</mat-label>
                <mat-select formControlName="assignee_user_id">
                  <mat-option value="">Unassigned</mat-option>
                  @for (user of organizationUsers; track user.id) {
                    <mat-option [value]="user.id">{{ user.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button type="submit" mat-flat-button color="primary" [disabled]="subtaskForm.invalid || saving()">Add</button>
            </form>
          }

              <div class="task-details__subtasks">
                @for (subtask of subtasks(); track subtask.id) {
                  <label class="task-details__subtask">
                    <mat-checkbox
                  [checked]="subtask.is_completed"
                  [disabled]="!canEdit || saving() || !canToggleSubtask(subtask)"
                  (change)="toggleSubtask(subtask, $event.checked)"
                    ></mat-checkbox>
                    <div>
                      @if (editingSubtaskId() === subtask.id) {
                        <form [formGroup]="subtaskEditForm" class="task-details__subtask-edit" (ngSubmit)="saveSubtaskTitle(subtask)">
                          <input type="text" formControlName="title" />
                          <mat-form-field appearance="outline">
                            <mat-label>Assignee</mat-label>
                            <mat-select formControlName="assignee_user_id">
                              <mat-option value="">Unassigned</mat-option>
                              @for (user of organizationUsers; track user.id) {
                                <mat-option [value]="user.id">{{ user.name }}</mat-option>
                              }
                            </mat-select>
                          </mat-form-field>
                          <div class="task-details__subtask-edit-actions">
                            <button type="submit" mat-button [disabled]="subtaskEditForm.invalid || saving()">Save</button>
                            <button type="button" mat-button (click)="cancelSubtaskEdit()">Cancel</button>
                          </div>
                        </form>
                      } @else {
                        <strong [class.task-details__subtask-title--done]="subtask.is_completed">{{ subtask.title }}</strong>
                      }
                      <span>{{ formatSubtaskMeta(subtask) }}</span>
                      @if (getSubtaskTimeByUser(subtask).length) {
                        <div class="task-details__subtask-time-breakdown">
                          @for (row of getSubtaskTimeByUser(subtask); track row.userId) {
                            <small>{{ row.name }}: {{ formatMinutes(row.minutes) }}</small>
                          }
                        </div>
                      }
                    </div>
                    @if (canEdit) {
                      <div class="task-details__subtask-row-actions">
                        <button type="button" mat-icon-button aria-label="Rename subtask" (click)="startSubtaskEdit(subtask)">
                          <mat-icon aria-hidden="true">edit</mat-icon>
                        </button>
                        <button type="button" mat-icon-button aria-label="Delete subtask" (click)="deleteSubtask(subtask)">
                          <mat-icon aria-hidden="true">delete</mat-icon>
                        </button>
                      </div>
                    }
                  </label>
                } @empty {
              <div class="task-details__empty">No subtasks yet.</div>
            }
          </div>
        </section>

        <section class="task-details__section">
          <div class="task-details__section-head">
            <h3>Comments</h3>
            <span>{{ comments().length }}</span>
          </div>

          <form [formGroup]="commentForm" class="task-details__comment-form" (ngSubmit)="addComment()">
            <mat-form-field appearance="outline">
              <mat-label>Add a comment</mat-label>
              <textarea matInput rows="3" formControlName="body"></textarea>
            </mat-form-field>
            <div class="task-details__comment-actions">
              <button type="submit" mat-flat-button color="primary" [disabled]="commentForm.invalid || saving()">Post comment</button>
            </div>
          </form>

          <div class="task-details__comments">
            @for (comment of comments(); track comment.id) {
              <article class="task-details__comment">
                <header>
                  <strong>{{ getUserName(comment.created_by) }}</strong>
                  <span>{{ formatDueDate(comment.created_at) }}</span>
                </header>
                <p>{{ comment.body }}</p>
              </article>
            } @empty {
              <div class="task-details__empty">No comments yet.</div>
            }
          </div>
        </section>
      </section>

      <aside class="task-details__activity">
        <div class="task-details__section-head">
          <h3>Activity</h3>
          <span>{{ activity().length }}</span>
        </div>

        <div class="task-details__timeline">
          @for (entry of activity(); track entry.id) {
            <article class="task-details__activity-item">
              <div class="task-details__activity-dot" aria-hidden="true"></div>
              <div>
                <strong>{{ entry.message }}</strong>
                <p>{{ getUserName(entry.actor_user_id) }} · {{ formatDueDate(entry.created_at) }}</p>
              </div>
            </article>
          } @empty {
            <div class="task-details__empty">No activity yet.</div>
          }
        </div>
      </aside>
    </div>
  }
</div>
